import React, { useEffect, useMemo, useRef, useState } from "react";
import { ComboBox, Item } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import { useTranslation } from "react-i18next";
import { useDependency } from "../../../../../../contexts/DependencyProvider";
import { ListItemRequest } from "../../../../../../services/soap/item/requests/ListItemRequest";
import { GetItemRequest } from "../../../../../../services/soap/item/requests/GetItemRequest";

interface ProductItem {
  id: string;
  name: string;
  refId?: string;
  description?: string;
  unitPrice?: any;
  otherUnitPrice?: any;
  catalogPrice?: any;
  vatValue?: any;
  VATValue?: any;
  currency?: any;
  currencyCode?: any;
  currencyName?: any;
}

interface IProductDropDownProps {
  selectedProduct: string;
  productName?: string;
  onChange: (data: ProductItem | null) => void;
  onInputValueChange?: (value: string) => void;
  isDisabled?: boolean;
  label?: string;
  width?: any;
}

const ProductDropDown: React.FC<IProductDropDownProps> = ({
  selectedProduct,
  productName,
  onChange,
  onInputValueChange,
  isDisabled,
  label,
  width = "100%",
}) => {
  const { store, itemService } = useDependency();
  const { t } = useTranslation("invoice");
  const resolvedLabel = label ?? t("invoice_transaction.product", { ns: "invoice", defaultValue: "Product" });
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [isUserEditing, setIsUserEditing] = useState(false);
  const [selectedFallbackItem, setSelectedFallbackItem] = useState<ProductItem | null>(null);

  const isUserEditingRef = useRef(false);

  const list = useAsyncList<ProductItem>({
    async load({ filterText }) {
      const q = String(filterText ?? "").trim();
      try {
        const byNamePromise = itemService.listItem(
          new ListItemRequest(store.Server, store.SessionId, 15, 0, {
            searchName: q,
            statusShow: "1",
          })
        );
        const byRefPromise =
          q.length > 0
            ? itemService.listItem(
                new ListItemRequest(store.Server, store.SessionId, 15, 0, {
                  refId: q,
                  statusShow: "1",
                })
              )
            : Promise.resolve({ ITEM: [] });

        const [byName, byRef]: any = await Promise.all([byNamePromise, byRefPromise]);
        const results: ProductItem[] = [];

        if (Array.isArray(byRef?.ITEM)) {
          byRef.ITEM.forEach((item: any) => {
            results.push({
              ...item,
              id: String(item.id),
              name: item.name ?? "",
            });
          });
        }

        if (Array.isArray(byName?.ITEM)) {
          byName.ITEM.forEach((item: any) => {
            const id = String(item.id);
            if (!results.some((r) => String(r.id) === id)) {
              results.push({
                ...item,
                id,
                name: item.name ?? "",
              });
            }
          });
        }

        return { items: results };
      } catch (error) {
        console.error("Error fetching products:", error);
        return { items: [] };
      }
    },
  });

  useEffect(() => {
    list.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const selectedId = String(selectedProduct ?? "").trim();
    if (!selectedId) {
      setSelectedFallbackItem(null);
      return;
    }

    // If selected item is already in current list, no fallback needed.
    if (list.items.some((x) => String(x.id) === selectedId)) {
      setSelectedFallbackItem(null);
      return;
    }

    let mounted = true;
    const loadSelectedProduct = async () => {
      try {
        const response: any = await itemService.getItem(
          new GetItemRequest(store.Server, store.SessionId, selectedId)
        );
        const item = response?.ITEM;
        if (!mounted || !item) return;
        setSelectedFallbackItem({
          ...item,
          id: String(item.id ?? selectedId),
          name: String(item.name ?? productName ?? ""),
        });
      } catch {
        if (!mounted) return;
        setSelectedFallbackItem({
          id: selectedId,
          name: String(productName ?? ""),
        });
      }
    };
    void loadSelectedProduct();
    return () => {
      mounted = false;
    };
  }, [selectedProduct, productName, list.items, itemService, store.Server, store.SessionId]);

  const mergedItems = useMemo(() => {
    if (!selectedFallbackItem) return list.items;
    const exists = list.items.some(
      (item) => String(item.id) === String(selectedFallbackItem.id)
    );
    if (exists) return list.items;
    return [selectedFallbackItem, ...list.items];
  }, [list.items, selectedFallbackItem]);

  useEffect(() => {
    if (isUserEditingRef.current || isUserEditing) return;
    if (!selectedProduct) {
      setSelectedKey(null);
      setInputValue(productName ?? "");
      return;
    }
    setSelectedKey(String(selectedProduct));
    const selected = mergedItems.find((x) => String(x.id) === String(selectedProduct));
    setInputValue(selected?.name ?? productName ?? "");
  }, [selectedProduct, productName, mergedItems, isUserEditing]);

  const handleInputChange = (value: string) => {
    setIsUserEditing(true);
    isUserEditingRef.current = true;
    setInputValue(value);
    list.setFilterText(value);
    onInputValueChange?.(value);

    if (selectedKey) {
      const selected = mergedItems.find((x) => String(x.id) === String(selectedKey));
      const selectedLabel = selected?.name ?? "";
      if (value !== selectedLabel) {
        setSelectedKey(null);
      }
    }

    if (!value.trim()) {
      setSelectedKey(null);
      list.setFilterText("");
      onChange(null);
    }
  };

  const handleSelectionChange = (key: React.Key | null) => {
    setIsUserEditing(false);
    isUserEditingRef.current = false;
    const idStr = key ? String(key) : null;
    setSelectedKey(idStr);
    if (!idStr) return;
    const selected = mergedItems.find((x) => String(x.id) === idStr);
    if (selected) {
      setInputValue(selected.name ?? "");
      onChange(selected);
      list.setFilterText("");
    }
  };

  return (
    <ComboBox
      label={resolvedLabel}
      aria-label={resolvedLabel}
      items={mergedItems}
      selectedKey={selectedKey}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
      loadingState={list.loadingState}
      allowsCustomValue
      isDisabled={isDisabled}
      width={width}
    >
      {(item) => (
        <Item key={item.id} textValue={item.name}>
          {item.refId ? `${item.name} (${item.refId})` : item.name}
        </Item>
      )}
    </ComboBox>
  );
};

export default ProductDropDown;
